import { Box, styled, Typography } from "@mui/material";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { LiveMonitorTile } from "./LiveMonitorTile";

import { ZoneBreadcrumbs } from "../../../../components/ZoneBreadcrumbs/ZoneBreadcrumbs";
import { useMonitoringStore } from "../../monitoringStore";
import { LiveDataTableProps } from "../utils/groupLiveData";

const Wrapper = styled("div")(({ theme }) => ({
  width: "100%",
  paddingInline: theme.spacing(1.5),
  paddingBlock: theme.spacing(1),
  borderColor: theme.vars.palette.borders.neutral.subtle,
  borderRadius: theme.spacing(1),
  borderStyle: "solid",
  borderWidth: 1,
  backgroundColor: theme.vars.palette.background.paper,
}));

const Grid = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: `repeat(auto-fit, ${theme.spacing(29)})`,
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: `1fr 1fr`,
  },
  "& > *": {
    minWidth: "auto",
    overflow: "hidden",
  },
}));

type MonitorZoneTilesProps = {
  liveData: LiveDataTableProps;
};

export function MonitorZoneTiles({ liveData }: MonitorZoneTilesProps) {
  const { t } = useTranslation();
  const scale = useMonitoringStore((s) => s.scale);
  const isFullscreen = useMonitoringStore((s) => s.isFullscreen);

  const computedScale = isFullscreen ? scale / 10 : 1;

  if (liveData.data.length == 0) {
    return (
      <Wrapper>
        <ZoneBreadcrumbs path={liveData.path} />
        <Typography
          paddingTop={1}
          variant={"bodyMicroRegular"}
          color={"textSecondary"}
        >
          {t("there_are_no_devices_in_this_zone")}
        </Typography>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <ZoneBreadcrumbs path={liveData.path} />
      <Box
        sx={{
          "--mui-spacing": computedScale * 8 + "px",
          fontSize: computedScale * 16 + "px",
        }}
      >
        <Grid>
          {liveData.data.map((item) =>
            item.subject ? (
              <Link
                key={item.device.id}
                to={`/subject/${item.subject.id}/sessions`}
                style={{ textDecoration: "none" }}
              >
                <LiveMonitorTile data={item} />
              </Link>
            ) : (
              <LiveMonitorTile key={item.device.id} data={item} />
            ),
          )}
        </Grid>
      </Box>
    </Wrapper>
  );
}
